import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import api from '../../api';
import { QUALITY_SERVICE_API_URL } from '../../config';
import DateRangePicker, { computeRangeForPreset } from '../Filters/DateRangePicker';

const BASE_PATH = `${QUALITY_SERVICE_API_URL}/hub/mail-forwarder-logs`;
const PAGE_SIZE = 25;
const DEBOUNCE_MS = 600;

const OUTCOMES = [
  { value: '', label: 'All outcomes' },
  { value: 'forwarded', label: 'Forwarded' },
  { value: 'no_matching_rule', label: 'No matching rule' },
  { value: 'failed', label: 'Failed' },
  { value: 'blocked', label: 'Blocked' },
];

const OUTCOME_BADGE = {
  forwarded: { label: 'Forwarded', className: 'mfl-outcome-forwarded' },
  no_matching_rule: { label: 'No matching rule', className: 'mfl-outcome-no_matching_rule' },
  failed: { label: 'Failed', className: 'mfl-outcome-failed' },
  blocked: { label: 'Blocked', className: 'mfl-outcome-blocked' },
};

const DEFAULT_TEXT_FILTERS = { senderAddress: '', sourceAddress: '', destinationAddress: '', subject: '' };

function defaultDateRange() {
  return { ...computeRangeForPreset('30d'), preset: '30d' };
}

function endOfDay(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

function fmtDateTime(d) {
  return new Date(d).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function AdminMailForwarderLog() {
  const navigate = useNavigate();

  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [outcome, setOutcome] = useState('');
  const [draft, setDraft] = useState(DEFAULT_TEXT_FILTERS);
  const [committedText, setCommittedText] = useState(DEFAULT_TEXT_FILTERS);
  const [page, setPage] = useState(1);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const debounceRef = useRef(null);
  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const handleTextChange = (key, value) => {
    setDraft(d => ({ ...d, [key]: value }));
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      setCommittedText(prev => ({ ...prev, [key]: value }));
    }, DEBOUNCE_MS);
  };

  const handleOutcomeChange = (value) => {
    setOutcome(value);
    setPage(1);
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    setPage(1);
  };

  const handleClearFilters = () => {
    clearTimeout(debounceRef.current);
    setDateRange(defaultDateRange());
    setOutcome('');
    setDraft(DEFAULT_TEXT_FILTERS);
    setCommittedText(DEFAULT_TEXT_FILTERS);
    setPage(1);
  };

  useEffect(() => {
    setLoading(true);
    setError(null);

    const params = {
      page,
      pageSize: PAGE_SIZE,
      occurredFrom: dateRange.startDate.toISOString(),
      occurredTo: endOfDay(dateRange.endDate).toISOString(),
    };
    if (outcome) params.outcome = outcome;
    if (committedText.senderAddress) params.senderAddress = committedText.senderAddress;
    if (committedText.sourceAddress) params.sourceAddress = committedText.sourceAddress;
    if (committedText.destinationAddress) params.destinationAddress = committedText.destinationAddress;
    if (committedText.subject) params.subject = committedText.subject;

    api.get(BASE_PATH, { params })
      .then(r => { setRows(r.data.data); setTotal(r.data.total); })
      .catch(() => setError('Failed to load mail forwarder log.'))
      .finally(() => setLoading(false));
  }, [page, dateRange, outcome, committedText]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="admin-campaigns">
      <div className="admin-section-toolbar">
        <button className="admin-btn-back" onClick={() => navigate('/admin/mail-forwarders')}>
          <ArrowLeft size={16} /> Back
        </button>
        <h2 className="admin-section-title">Mail Forwarder Log</h2>
      </div>

      <div className="admin-filter-bar">
        <div className="admin-filter-group">
          <span className="admin-filter-label">Date range</span>
          <DateRangePicker
            value={dateRange}
            preset={dateRange.preset}
            onChange={handleDateRangeChange}
            enabledPresets={['7d', '14d', '30d', 'custom']}
          />
        </div>
        <div className="admin-filter-group">
          <label className="admin-filter-label" htmlFor="mfl-outcome">Outcome</label>
          <select id="mfl-outcome" className="cf-input" value={outcome} onChange={e => handleOutcomeChange(e.target.value)}>
            {OUTCOMES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="admin-filter-group">
          <label className="admin-filter-label" htmlFor="mfl-sender">Sender</label>
          <input id="mfl-sender" className="cf-input" type="text" placeholder="sender@example.com"
            value={draft.senderAddress} onChange={e => handleTextChange('senderAddress', e.target.value)} />
        </div>
        <div className="admin-filter-group">
          <label className="admin-filter-label" htmlFor="mfl-source">Source alias</label>
          <input id="mfl-source" className="cf-input" type="text" placeholder="alias@contentsquare.cloud"
            value={draft.sourceAddress} onChange={e => handleTextChange('sourceAddress', e.target.value)} />
        </div>
        <div className="admin-filter-group">
          <label className="admin-filter-label" htmlFor="mfl-dest">Destination</label>
          <input id="mfl-dest" className="cf-input" type="text" placeholder="dest@contentsquare.com"
            value={draft.destinationAddress} onChange={e => handleTextChange('destinationAddress', e.target.value)} />
        </div>
        <div className="admin-filter-group">
          <label className="admin-filter-label" htmlFor="mfl-subject">Subject</label>
          <input id="mfl-subject" className="cf-input" type="text" placeholder="password reset"
            value={draft.subject} onChange={e => handleTextChange('subject', e.target.value)} />
        </div>
        <div className="admin-filter-actions">
          <button className="admin-btn-secondary" onClick={handleClearFilters}>Clear filters</button>
        </div>
      </div>

      {error && <div className="admin-error">{error}</div>}

      {loading ? (
        <div className="admin-loading">Loading…</div>
      ) : (
        <>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Occurred</th>
                  <th>Sender</th>
                  <th>Source</th>
                  <th>Destinations</th>
                  <th>Subject</th>
                  <th>Outcome</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr key={row.id}>
                    <td>{fmtDateTime(row.occurredAt)}</td>
                    <td>{row.senderAddress ?? '—'}</td>
                    <td className="admin-td-name">{row.sourceAddress}</td>
                    <td>
                      <div className="admin-pill-list">
                        {(row.destinationAddresses ?? []).map(addr => (
                          <code key={addr} className="admin-label-chip">{addr}</code>
                        ))}
                        {(!row.destinationAddresses || row.destinationAddresses.length === 0) && '—'}
                      </div>
                    </td>
                    <td className="mfl-td-ellipsis" title={row.subject ?? ''}>{row.subject ?? '—'}</td>
                    <td>
                      <span className={`mfl-outcome-badge ${OUTCOME_BADGE[row.outcome]?.className ?? ''}`}>
                        {OUTCOME_BADGE[row.outcome]?.label ?? row.outcome}
                      </span>
                    </td>
                    <td className="mfl-td-ellipsis" title={row.errorDetail ?? ''}>{row.errorDetail ?? '—'}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={7} className="admin-empty">No log entries match these filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="admin-pagination">
            <span>
              {total === 0 ? '0 results' : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total}`}
            </span>
            <button className="admin-pagination-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</button>
            <span>Page {page} of {totalPages}</span>
            <button className="admin-pagination-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
          </div>
        </>
      )}
    </div>
  );
}

export default AdminMailForwarderLog;
